import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, Phone, Mail, Globe } from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface Club {
  id: string;
  name: string;
  address?: string;
  contact?: string;
  telegramGroupChatId?: string;
  phone?: string;
  email?: string;
  website?: string;
  rates?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  telegramContacts?: Array<{
    id: string;
    telegramUserId: string;
    displayName: string;
    username?: string | null;
    position?: string | null;
    isActive: boolean;
    lastResponseAt?: string | null;
  }>;
  tournaments?: Array<{
    id: string;
    name: string;
    startDate: string;
    status: string;
  }>;
  courts?: Array<{
    id: string;
    name: string;
    surfaceType?: string;
    isActive: boolean;
  }>;
  serviceProviders?: Array<{
    id: string;
    name: string;
    contact?: string;
    phone?: string;
    email?: string;
    address?: string;
    isActive: boolean;
  }>;
}

interface ClubFormData {
  name: string;
  address: string;
  contact: string;
  telegramGroupChatId: string;
  phone: string;
  email: string;
  website: string;
  rates: string;
  isActive: boolean;
}

const Clubs: React.FC = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingClub, setEditingClub] = useState<Club | null>(null);
  const [formData, setFormData] = useState<ClubFormData>({
    name: '',
    address: '',
    contact: '',
    telegramGroupChatId: '',
    phone: '',
    email: '',
    website: '',
    rates: '',
    isActive: true
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCourts, setShowCourts] = useState<string | null>(null);
  const [courts, setCourts] = useState<Array<{ id: string; name: string; surfaceType?: string; isActive: boolean }>>([]);
  const [showProviders, setShowProviders] = useState<string | null>(null);
  const [providers, setProviders] = useState<Array<{ id: string; name: string; contact?: string; phone?: string; email?: string; address?: string; isActive: boolean }>>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clubToDelete, setClubToDelete] = useState<Club | null>(null);
  const [showSuppliers, setShowSuppliers] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string; contact?: string; phone?: string; email?: string; address?: string; isActive: boolean }>>([]);
  const [showTelegramContacts, setShowTelegramContacts] = useState<string | null>(null);
  const [telegramContacts, setTelegramContacts] = useState<Array<{
    id: string;
    telegramUserId: string;
    displayName: string;
    username?: string | null;
    position?: string | null;
    isActive: boolean;
    lastResponseAt?: string | null;
  }>>([]);

  // Функция для подсчета кортов по типам
  const getCourtsByType = (courts: Array<{ surfaceType?: string; isActive: boolean }>) => {
    const activeCourts = courts.filter(court => court.isActive);
    const counts: { [key: string]: number } = {};
    
    activeCourts.forEach(court => {
      const type = court.surfaceType || 'Не указан';
      counts[type] = (counts[type] || 0) + 1;
    });
    
    return counts;
  };

  // Функция для получения названия типа покрытия
  const getSurfaceTypeName = (type: string) => {
    const names: { [key: string]: string } = {
      'hard': 'Хард',
      'clay': 'Грунт', 
      'grass': 'Трава',
      'synthetic': 'Синтетика',
      'Не указан': 'Не указан'
    };
    return names[type] || type;
  };

  // Загрузка клубов
  const fetchClubs = async () => {
    try {
      const data = await apiGet<{ clubs: Club[] }>('/clubs');
      const clubsData = data.clubs || [];
      
      // Загружаем корты и контрагентов для каждого клуба
      const clubsWithData = await Promise.all(
        clubsData.map(async (club: Club) => {
          try {
            const [courtsData, providersData] = await Promise.all([
              apiGet<{ items?: any[] }>(`/courts?clubId=${club.id}`),
              apiGet<{ items?: any[] }>(`/service-providers?clubId=${club.id}`)
            ]);
            
            return {
              ...club,
              courts: courtsData.items || [],
              serviceProviders: providersData.items || []
            };
          } catch (error) {
            console.error(`Error fetching data for club ${club.id}:`, error);
            return {
              ...club,
              courts: [],
              serviceProviders: []
            };
          }
        })
      );
      
      setClubs(clubsWithData);
    } catch (error) {
      console.error('Error fetching clubs:', error);
    }
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  // Обработка отправки формы
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Парсим rates как JSON
      let parsedRates = null;
      if (formData.rates.trim()) {
        try {
          parsedRates = JSON.parse(formData.rates);
        } catch (error) {
          setError('Неверный формат JSON для тарифов');
          setIsLoading(false);
          return;
        }
      }

      const data = editingClub
        ? await apiPut<{ club: Club }>(`/clubs/${editingClub.id}`, { ...formData, rates: parsedRates })
        : await apiPost<{ club: Club }>('/clubs', { ...formData, rates: parsedRates });

      if (editingClub) {
        setClubs(clubs.map(club => club.id === editingClub.id ? data.club : club));
      } else {
        setClubs([...clubs, data.club]);
      }
      handleCloseForm();
    } catch (error: any) {
      console.error('Error saving club:', error);
      setError(error.message || 'Ошибка при сохранении клуба');
    } finally {
      setIsLoading(false);
    }
  };

  // Обработка редактирования
  const handleEdit = (club: Club) => {
    setEditingClub(club);
    setFormData({
      name: club.name,
      address: club.address || '',
      contact: club.contact || '',
      telegramGroupChatId: club.telegramGroupChatId || '',
      phone: club.phone || '',
      email: club.email || '',
      website: club.website || '',
      rates: club.rates ? JSON.stringify(JSON.parse(club.rates), null, 2) : '',
      isActive: club.isActive
    });
    setShowForm(true);
  };

  // Обработка удаления
  const handleDelete = async (club: Club) => {
    setClubToDelete(club);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!clubToDelete) return;

    try {
      await apiDelete(`/clubs/${clubToDelete.id}`);
      setClubs(clubs.filter(club => club.id !== clubToDelete.id));
      setShowDeleteDialog(false);
      setClubToDelete(null);
    } catch (error: any) {
      console.error('Error deleting club:', error);
      setError(error.message || 'Ошибка при удалении клуба');
    }
嘶
  };

  // Закрытие формы
  const handleCloseForm = () => {
    setShowForm(false);
    setEditingClub(null);
    setFormData({
      name: '',
      address: '',
      contact: '',
      telegramGroupChatId: '',
      phone: '',
      email: '',
      website: '',
      rates: '',
      isActive: true
    });
    setError(null);
  };

  // Загрузка кортов клуба
  const fetchCourts = async (clubId: string) => {
    try {
      const data = await apiGet<{ items?: any[] }>(`/courts?clubId=${clubId}`);
      setCourts(data.items || []);
    } catch (error) {
      console.error('Error fetching courts:', error);
      setError('Ошибка при загрузке кортов');
    }
  };

  // Показать корты клуба
  const handleShowCourts = (club: Club) => {
    setShowCourts(club.id);
    fetchCourts(club.id);
  };

  // Показать контрагентов клуба
  const handleShowProviders = (club: Club) => {
    setShowProviders(club.id);
    fetchProviders(club.id);
  };

  const handleShowSuppliers = (club: Club) => {
    setShowSuppliers(club.id);
    fetchSuppliers(club.id);
  };

  const fetchTelegramContacts = async (clubId: string) => {
    try {
      const data = await apiGet<{ contacts?: Array<{
        id: string;
        telegramUserId: string;
        displayName: string;
        username?: string | null;
        position?: string | null;
        isActive: boolean;
        lastResponseAt?: string | null;
      }> }>(`/clubs/${clubId}/telegram-contacts`);
      setTelegramContacts(data.contacts || []);
    } catch (error) {
      console.error('Error fetching telegram contacts:', error);
      setError('Ошибка при загрузке Telegram-контактов клуба');
    }
  };

  const handleShowTelegramContacts = (club: Club) => {
    setShowTelegramContacts(club.id);
    fetchTelegramContacts(club.id);
  };

  // Создать корт
  const handleCreateCourt = async (clubId: string, name: string, surfaceType: string) => {
    try {
      await apiPost('/courts', { name, surfaceType, clubId });
      await fetchCourts(clubId);
      // Обновляем корты в основном списке клубов
      await fetchClubs();
    } catch (error: any) {
      console.error('Error creating court:', error);
      setError(error.message || 'Ошибка при создании корта');
    }
  };

  // Обновить корт
  const handleUpdateCourt = async (courtId: string, clubId: string, name: string, surfaceType: string) => {
    try {
      await apiPut(`/courts/${courtId}`, { name, surfaceType, clubId });
      await fetchCourts(clubId);
      // Обновляем корты в основном списке клубов
      await fetchClubs();
    } catch (error: any) {
      console.error('Error updating court:', error);
      setError(error.message || 'Ошибка при обновлении корта');
    }
  };

  // Удалить корт
  const handleDeleteCourt = async (courtId: string, clubId: string) => {
    try {
      await apiDelete(`/courts/${courtId}`);
      await fetchCourts(clubId);
      // Обновляем корты в основном списке клубов
      await fetchClubs();
    } catch (error: any) {
      console.error('Error deleting court:', error);
      setError(error.message || 'Ошибка при удалении корта');
    }
  };

  // Загрузка контрагентов клуба
  const fetchProviders = async (clubId: string) => {
    try {
      const data = await apiGet<{ items?: any[] }>(`/service-providers?clubId=${clubId}`);
      setProviders(data.items || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      setError('Ошибка при загрузке контрагентов');
    }
  };

  // Загрузка поставщиков (контрагентов) для клуба
  const fetchSuppliers = async (clubId: string) => {
    try {
      const data = await apiGet<{ suppliers?: any[] }>(`/suppliers?clubId=${clubId}&isActive=true`);
      setSuppliers(data.suppliers || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setError('Ошибка при загрузке поставщиков');
    }
  };

  const handleCreateTelegramContact = async (
    clubId: string,
    payload: { telegramUserId: string; displayName: string; username?: string; position?: string; isActive: boolean }
  ) => {
    try {
      await apiPost(`/clubs/${clubId}/telegram-contacts`, payload);
      await fetchTelegramContacts(clubId);
      await fetchClubs();
    } catch (error: any) {
      console.error('Error creating telegram contact:', error);
      setError(error.message || 'Ошибка при создании Telegram-контакта');
    }
  };

  const handleUpdateTelegramContact = async (
    clubId: string,
    contactId: string,
    payload: { telegramUserId: string; displayName: string; username?: string; position?: string; isActive: boolean }
  ) => {
    try {
      await apiPut(`/clubs/${clubId}/telegram-contacts/${contactId}`, payload);
      await fetchTelegramContacts(clubId);
      await fetchClubs();
    } catch (error: any) {
      console.error('Error updating telegram contact:', error);
      setError(error.message || 'Ошибка при обновлении Telegram-контакта');
    }
  };

  const handleDeleteTelegramContact = async (clubId: string, contactId: string) => {
    try {
      await apiDelete(`/clubs/${clubId}/telegram-contacts/${contactId}`);
      await fetchTelegramContacts(clubId);
      await fetchClubs();
    } catch (error: any) {
      console.error('Error deleting telegram contact:', error);
      setError(error.message || 'Ошибка при удалении Telegram-контакта');
    }
  };

  // Создать контрагента
  const handleCreateProvider = async (clubId: string, name: string, contact: string, phone: string, email: string, address: string) => {
    try {
      await apiPost('/service-providers', { name, contact, phone, email, address, clubId });
      await fetchProviders(clubId);
      // Обновляем контрагентов в основном списке клубов
      await fetchClubs();
    } catch (error: any) {
      console.error('Error creating provider:', error);
      setError(error.message || 'Ошибка при создании контрагента');
    }
  };

  // Обновить контрагента
  const handleUpdateProvider = async (providerId: string, clubId: string, name: string, contact: string, phone: string, email: string, address: string) => {
    try {
      await apiPut(`/service-providers/${providerId}`, { name, contact, phone, email, address, clubId });
      await fetchProviders(clubId);
      // Обновляем контрагентов в основном списке клубов
      await fetchClubs();
    } catch (error: any) {
      console.error('Error updating provider:', error);
      setError(error.message || 'Ошибка при обновлении контрагента');
    }
  };

  // Удалить контрагента
  const handleDeleteProvider = async (providerId: string, clubId: string) => {
    try {
      await apiDelete(`/service-providers/${providerId}`);
      await fetchProviders(clubId);
      // Обновляем контрагентов в основном списке клубов
      await fetchClubs();
    } catch (error: any) {
      console.error('Error deleting provider:', error);
      setError(error.message || 'Ошибка при удалении контрагента');
    }
  };

  // Обработка изменения полей формы
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Заголовок */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Теннисные клубы</h1>
            <p className="text-gray-600 mt-2">Управление клубами и тарифами</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Добавить клуб</span>
          </button>
        </div>

        {/* Список клубов */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clubs.map((club) => (
            <div key={club.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900">{club.name}</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleShowCourts(club)}
                    className="text-green-600 hover:text-green-800"
                    title="Корты"
                  >
                    🏟️
                  </button>
                  <button
                    onClick={() => handleShowSuppliers(club)}
                    className="text-purple-600 hover:text-purple-800"
                    title="Контрагенты"
                  >
                    🏢
                  </button>
                  <button
                    onClick={() => handleShowTelegramContacts(club)}
                    className="text-teal-600 hover:text-teal-800"
                    title="Telegram-контакты клуба"
                  >
                    👥
                  </button>
                  <button
                    onClick={() => handleEdit(club)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Редактировать"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  {/* Показываем кнопку удаления только если нет связанных данных */}
                  {(!club.tournaments || club.tournaments.length === 0) && 
                   (!club.courts || club.courts.length === 0) && 
                   (!club.serviceProviders || club.serviceProviders.length === 0) && (
                    <button
                      onClick={() => handleDelete(club)}
                      className="text-red-600 hover:text-red-800"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                {club.address && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4" />
                    <span>{club.address}</span>
                  </div>
                )}
                {club.contact && (
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">Контакт:</span>
                    <span>{club.contact}</span>
                  </div>
                )}
                {club.telegramGroupChatId && (
                  <div className="flex items-center space-x-2">
                    <span className="font-medium">Telegram Group ID:</span>
                    <span className="font-mono">{club.telegramGroupChatId}</span>
                  </div>
                )}
                {club.telegramContacts && club.telegramContacts.length > 0 && (
                  <div className="space-y-1">
                    <div className="font-medium">Telegram-контакты клуба:</div>
                    {club.telegramContacts.slice(0, 3).map((contact) => (
                      <div key={contact.id} className="text-xs">
                        • {contact.displayName}
                        {contact.position ? ` (${contact.position})` : ''}
                        {contact.username ? ` ${contact.username}` : ''}
                      </div>
                    ))}
                    {club.telegramContacts.length > 3 && (
                      <div className="text-xs text-gray-500">и еще {club.telegramContacts.length - 3}</div>
                    )}
                  </div>
                )}
                {club.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="w-4 h-4" />
                    <span>{club.phone}</span>
                  </div>
                )}
                {club.email && (
                  <div className="flex items-center space-x-2">
                    <Mail className="w-4 h-4" />
                    <span>{club.email}</span>
                  </div>
                )}
                {club.website && (
                  <div className="flex items-center space-x-2">
                    <Globe className="w-4 h-4" />
                    <a href={club.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {club.website}
                    </a>
                  </div>
                )}
              </div>

              {club.rates && (() => {
                try {
                  const rates = JSON.parse(club.rates);
                  return (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-900 mb-2">Тарифы:</h4>
                      <div className="space-y-1">
                        {Object.entries(rates).map(([key, value]) => (
                          <div key={key} className="flex justify-between items-center text-sm">
                            <span className="text-blue-800 capitalize">
                              {key === 'courtRental' ? 'Аренда корта' :
                               key === 'lighting' ? 'Освещение' :
                               key === 'equipment' ? 'Оборудование' :
                               key}
                            </span>
                            <span className="font-medium text-blue-900">
                              {value} MDL
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                } catch (error) {
                  return (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg">
                      <h4 className="font-medium text-red-900 mb-2">Тарифы:</h4>
                      <p className="text-sm text-red-600">Ошибка в формате тарифов</p>
                    </div>
                  );
                }
              })()}

              {/* Информация о кортах */}
              {club.courts && club.courts.length > 0 && (() => {
                const courtsByType = getCourtsByType(club.courts);
                const totalCourts = Object.values(courtsByType).reduce((sum, count) => sum + count, 0);
                
                return (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">
                      Корты ({totalCourts}):
                    </h4>
                    <div className="space-y-1">
                      {Object.entries(courtsByType).map(([type, count]) => (
                        <div key={type} className="flex justify-between items-center text-sm">
                          <span className="text-green-800">
                            {getSurfaceTypeName(type)}
                          </span>
                          <span className="font-medium text-green-900">
                            {count}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}


              <div className="mt-4 flex justify-between items-center">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  club.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {club.isActive ? 'Активен' : 'Неактивен'}
                </span>
                {club.tournaments && club.tournaments.length > 0 && (
                  <span className="text-sm text-gray-500">
                    {club.tournaments.length} турнир(ов)
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {clubs.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <MapPin className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Клубы не найдены</h3>
            <p className="text-gray-500">Добавьте первый клуб, чтобы начать работу</p>
          </div>
        )}
      </div>

      {/* Форма создания/редактирования */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingClub ? 'Редактировать клуб' : 'Добавить клуб'}
              </h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Название клуба *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Адрес
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Контактное лицо
                    </label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telegram Group ID
                    </label>
                    <input
                      type="text"
                      name="telegramGroupChatId"
                      value={formData.telegramGroupChatId}
                      onChange={handleInputChange}
                      placeholder="-1001234567890"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Chat ID Telegram-группы клуба, куда отправлять запросы на резерв корта.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Телефон
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Веб-сайт
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Тарифы (JSON)
                  </label>
                  <textarea
                    name="rates"
                    value={formData.rates}
                    onChange={handleInputChange}
                    rows={6}
                    placeholder='{"courtRental": 100, "lighting": 50, "equipment": 30}'
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Введите тарифы в формате JSON. Например: {`{"courtRental": 100, "lighting": 50, "equipment": 30}`}
                  </p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Клуб активен
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isLoading ? 'Сохранение...' : (editingClub ? 'Обновить' : 'Создать')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Диалог подтверждения удаления */}
      {showDeleteDialog && clubToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Подтвердите удаление
              </h3>
              <p className="text-gray-600 mb-6">
                Вы уверены, что хотите удалить клуб "{clubToDelete.name}"? 
                Это действие нельзя отменить.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteDialog(false);
                    setClubToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Отмена
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно управления кортами */}
      {showCourts && (
        <CourtsModal
          clubId={showCourts}
          clubName={clubs.find(c => c.id === showCourts)?.name || ''}
          courts={courts}
          onClose={() => setShowCourts(null)}
          onCreateCourt={handleCreateCourt}
          onUpdateCourt={handleUpdateCourt}
          onDeleteCourt={handleDeleteCourt}
        />
      )}

      {/* Модальное окно управления контрагентами */}
      {showProviders && (
        <ProvidersModal
          clubId={showProviders}
          clubName={clubs.find(c => c.id === showProviders)?.name || ''}
          providers={providers}
          onClose={() => setShowProviders(null)}
          onCreateProvider={handleCreateProvider}
          onUpdateProvider={handleUpdateProvider}
          onDeleteProvider={handleDeleteProvider}
        />
      )}

      {showSuppliers && (
        <SuppliersModal
          clubId={showSuppliers}
          clubName={clubs.find(c => c.id === showSuppliers)?.name || ''}
          suppliers={suppliers}
          onClose={() => setShowSuppliers(null)}
        />
      )}

      {showTelegramContacts && (
        <TelegramContactsModal
          clubId={showTelegramContacts}
          clubName={clubs.find(c => c.id === showTelegramContacts)?.name || ''}
          contacts={telegramContacts}
          onClose={() => setShowTelegramContacts(null)}
          onCreateContact={handleCreateTelegramContact}
          onUpdateContact={handleUpdateTelegramContact}
          onDeleteContact={handleDeleteTelegramContact}
        />
      )}
    </div>
  );
};

// Компонент модального окна управления кортами
const CourtsModal: React.FC<{
  clubId: string;
  clubName: string;
  courts: Array<{ id: string; name: string; surfaceType?: string; isActive: boolean }>;
  onClose: () => void;
  onCreateCourt: (clubId: string, name: string, surfaceType: string) => void;
  onUpdateCourt: (courtId: string, clubId: string, name: string, surfaceType: string) => void;
  onDeleteCourt: (courtId: string, clubId: string) => void;
}> = ({ clubId, clubName, courts, onClose, onCreateCourt, onUpdateCourt, onDeleteCourt }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCourt, setEditingCourt] = useState<{ id: string; name: string; surfaceType: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    surfaceType: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCourt) {
      onUpdateCourt(editingCourt.id, clubId, formData.name, formData.surfaceType);
    } else {
      onCreateCourt(clubId, formData.name, formData.surfaceType);
    }
    setShowForm(false);
    setEditingCourt(null);
    setFormData({ name: '', surfaceType: '' });
  };

  const handleEdit = (court: { id: string; name: string; surfaceType?: string }) => {
    setEditingCourt({ id: court.id, name: court.name, surfaceType: court.surfaceType || '' });
    setFormData({ name: court.name, surfaceType: court.surfaceType || '' });
    setShowForm(true);
  };

  const handleDelete = (courtId: string) => {
    if (confirm('Удалить корт?')) {
      onDeleteCourt(courtId, clubId);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Корты клуба "{clubName}"
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Добавить корт
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Закрыть
              </button>
            </div>
          </div>

          {/* Список кортов */}
          <div className="space-y-4">
            {courts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Корты не найдены
              </div>
            ) : (
              courts.map((court) => (
                <div key={court.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">{court.name}</h3>
                    {court.surfaceType && (
                      <p className="text-sm text-gray-500">Тип покрытия: {court.surfaceType}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(court)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(court.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Форма создания/редактирования корта */}
          {showForm && (
            <div className="mt-6 p-4 border border-gray-200 rounded-lg">
              <h3 className="text-lg font-medium mb-4">
                {editingCourt ? 'Редактировать корт' : 'Добавить корт'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Название корта *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Тип покрытия
                  </label>
                  <select
                    value={formData.surfaceType}
                    onChange={(e) => setFormData(prev => ({ ...prev, surfaceType: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Выберите тип покрытия</option>
                    <option value="hard">Хард</option>
                    <option value="clay">Грунт</option>
                    <option value="grass">Трава</option>
                    <option value="synthetic">Синтетика</option>
                  </select>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingCourt ? 'Сохранить' : 'Добавить'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingCourt(null);
                      setFormData({ name: '', surfaceType: '' });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Отмена
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Компонент модального окна управления контрагентами
const ProvidersModal: React.FC<{
  clubId: string;
  clubName: string;
  providers: Array<{ id: string; name: string; contact?: string; phone?: string; email?: string; address?: string; isActive: boolean }>;
  onClose: () => void;
  onCreateProvider: (clubId: string, name: string, contact: string, phone: string, email: string, address: string) => void;
  onUpdateProvider: (providerId: string, clubId: string, name: string, contact: string, phone: string, email: string, address: string) => void;
  onDeleteProvider: (providerId: string, clubId: string) => void;
}> = ({ clubId, clubName, providers, onClose, onCreateProvider, onUpdateProvider, onDeleteProvider }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<{ id: string; name: string; contact: string; phone: string; email: string; address: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    phone: '',
    email: '',
    address: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProvider) {
      onUpdateProvider(editingProvider.id, clubId, formData.name, formData.contact, formData.phone, formData.email, formData.address);
    } else {
      onCreateProvider(clubId, formData.name, formData.contact, formData.phone, formData.email, formData.address);
    }
    setShowForm(false);
    setEditingProvider(null);
    setFormData({ name: '', contact: '', phone: '', email: '', address: '' });
  };

  const handleEdit = (provider: { id: string; name: string; contact?: string; phone?: string; email?: string; address?: string }) => {
    setEditingProvider({ 
      id: provider.id, 
      name: provider.name, 
      contact: provider.contact || '', 
      phone: provider.phone || '', 
      email: provider.email || '', 
      address: provider.address || '' 
    });
    setFormData({ 
      name: provider.name, 
      contact: provider.contact || '', 
      phone: provider.phone || '', 
      email: provider.email || '', 
      address: provider.address || '' 
    });
    setShowForm(true);
  };

  const handleDelete = (providerId: string) => {
    if (confirm('Удалить контрагента?')) {
      onDeleteProvider(providerId, clubId);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Контрагенты клуба "{clubName}"
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Добавить контрагента
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Закрыть
              </button>
            </div>
          </div>

          {/* Список контрагентов */}
          <div className="space-y-4">
            {providers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Контрагенты не найдены
              </div>
            ) : (
              providers.map((provider) => (
                <div key={provider.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">{provider.name}</h3>
                    {provider.contact && (
                      <p className="text-sm text-gray-500">Контакт: {provider.contact}</p>
                    )}
                    {provider.phone && (
                      <p className="text-sm text-gray-500">Телефон: {provider.phone}</p>
                    )}
                    {provider.email && (
                      <p className="text-sm text-gray-500">Email: {provider.email}</p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(provider)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(provider.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Форма создания/редактирования контрагента */}
          {showForm && (
            <div className="mt-6 p-4 border border-gray-200 rounded-lg">
              <h3 className="text-lg font-medium mb-4">
                {editingProvider ? 'Редактировать контрагента' : 'Добавить контрагента'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Название *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Контактное лицо
                    </label>
                    <input
                      type="text"
                      value={formData.contact}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Телефон
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Адрес
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingProvider ? 'Сохранить' : 'Добавить'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingProvider(null);
                      setFormData({ name: '', contact: '', phone: '', email: '', address: '' });
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Отмена
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Компонент модального окна управления Telegram-контактами клуба
const TelegramContactsModal: React.FC<{
  clubId: string;
  clubName: string;
  contacts: Array<{
    id: string;
    telegramUserId: string;
    displayName: string;
    username?: string | null;
    position?: string | null;
    isActive: boolean;
    lastResponseAt?: string | null;
  }>;
  onClose: () => void;
  onCreateContact: (
    clubId: string,
    payload: { telegramUserId: string; displayName: string; username?: string; position?: string; isActive: boolean }
  ) => void;
  onUpdateContact: (
    clubId: string,
    contactId: string,
    payload: { telegramUserId: string; displayName: string; username?: string; position?: string; isActive: boolean }
  ) => void;
  onDeleteContact: (clubId: string, contactId: string) => void;
}> = ({ clubId, clubName, contacts, onClose, onCreateContact, onUpdateContact, onDeleteContact }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState<{
    id: string;
    telegramUserId: string;
    displayName: string;
    username?: string | null;
    position?: string | null;
    isActive: boolean;
  } | null>(null);
  const [formData, setFormData] = useState({
    telegramUserId: '',
    displayName: '',
    username: '',
    position: '',
    isActive: true
  });

  const resetForm = () => {
    setFormData({
      telegramUserId: '',
      displayName: '',
      username: '',
      position: '',
      isActive: true
    });
    setEditingContact(null);
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      telegramUserId: formData.telegramUserId.trim(),
      displayName: formData.displayName.trim(),
      username: formData.username.trim(),
      position: formData.position.trim(),
      isActive: formData.isActive
    };

    if (editingContact) {
      onUpdateContact(clubId, editingContact.id, payload);
    } else {
      onCreateContact(clubId, payload);
    }
    resetForm();
  };

  const handleEdit = (contact: {
    id: string;
    telegramUserId: string;
    displayName: string;
    username?: string | null;
    position?: string | null;
    isActive: boolean;
  }) => {
    setEditingContact(contact);
    setFormData({
      telegramUserId: contact.telegramUserId,
      displayName: contact.displayName,
      username: contact.username || '',
      position: contact.position || '',
      isActive: contact.isActive
    });
    setShowForm(true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Telegram-контакты клуба "{clubName}"
            </h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Добавить контакт
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Закрыть
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {contacts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Telegram-контакты пока не добавлены
              </div>
            ) : (
              contacts.map((contact) => (
                <div key={contact.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">{contact.displayName}</h3>
                    <p className="text-sm text-gray-600">Telegram ID: <span className="font-mono">{contact.telegramUserId}</span></p>
                    {contact.position && (
                      <p className="text-sm text-gray-600">Должность: {contact.position}</p>
                    )}
                    {contact.username && (
                      <p className="text-sm text-gray-600">Username: {contact.username}</p>
                    )}
                    {contact.lastResponseAt && (
                      <p className="text-xs text-gray-500">
                        Последний ответ: {new Date(contact.lastResponseAt).toLocaleString('ru-RU')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      contact.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {contact.isActive ? 'Активен' : 'Неактивен'}
                    </span>
                    <button onClick={() => handleEdit(contact)} className="text-blue-600 hover:text-blue-800">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDeleteContact(clubId, contact.id)} className="text-red-600 hover:text-red-800">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {showForm && (
            <div className="mt-6 p-4 border border-gray-200 rounded-lg">
              <h3 className="text-lg font-medium mb-4">
                {editingContact ? 'Редактировать контакт' : 'Добавить контакт'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telegram User ID *</label>
                    <input
                      type="text"
                      value={formData.telegramUserId}
                      onChange={(e) => setFormData(prev => ({ ...prev, telegramUserId: e.target.value }))}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Имя/ФИО *</label>
                    <input
                      type="text"
                      value={formData.displayName}
                      onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                      required
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="@acvila_admin"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Должность</label>
                    <input
                      type="text"
                      value={formData.position}
                      onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                      placeholder="Manager / Administrator"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                <div className="flex items-center">
                  <input
                    id="telegram-contact-is-active"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="telegram-contact-is-active" className="ml-2 text-sm text-gray-700">
                    Контакт активен
                  </label>
                </div>
                <div className="flex space-x-2">
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    {editingContact ? 'Сохранить' : 'Добавить'}
                  </button>
                  <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600">
                    Отмена
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Компонент модального окна управления контрагентами (поставщиками)
const SuppliersModal: React.FC<{
  clubId: string;
  clubName: string;
  suppliers: Array<{ id: string; name: string; contact?: string; phone?: string; email?: string; address?: string; isActive: boolean }>;
  onClose: () => void;
}> = ({ clubId, clubName, suppliers, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Контрагенты клуба "{clubName}"
            </h2>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              Закрыть
            </button>
          </div>

          {/* Список контрагентов */}
          <div className="space-y-4">
            {suppliers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>У этого клуба пока нет контрагентов</p>
                <p className="text-sm mt-2">
                  Контрагенты добавляются через справочник "Контрагенты" с указанием клуба
                </p>
              </div>
            ) : (
              suppliers.map((supplier) => (
                <div key={supplier.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{supplier.name}</h3>
                      {supplier.contact && (
                        <p className="text-sm text-gray-600 mt-1">Контакт: {supplier.contact}</p>
                      )}
                      {supplier.phone && (
                        <p className="text-sm text-gray-600">Телефон: {supplier.phone}</p>
                      )}
                      {supplier.email && (
                        <p className="text-sm text-gray-600">Email: {supplier.email}</p>
                      )}
                      {supplier.address && (
                        <p className="text-sm text-gray-600">Адрес: {supplier.address}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        supplier.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {supplier.isActive ? 'Активен' : 'Неактивен'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clubs;
